import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CustomerProfileData } from '@compiere-ws/models/apiz-domains';
import { LoadingDirective } from '@iupics-components/directives/loading.directive';
import InputStarsUiComponent from '@iupics-components/standard/fields/input-stars-ui/input-stars-ui.component';
import AvatarUiComponent from '@iupics-components/standard/user/avatar-ui/avatar-ui.component';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { DocServerUrlPipe } from '@iupics-util/pipes/doc-server-url/doc-server-url.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { defer, from, iif, map, of, startWith, switchMap } from 'rxjs';

@Component({
  selector: 'iu-customer-profile',
  templateUrl: './customer-profile.component.html',
  styleUrls: ['./customer-profile.component.scss'],
  standalone: true,
  imports: [NgClass, LoadingDirective, InputStarsUiComponent, TranslateModule, AvatarUiComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class CustomerProfileComponent {
  protected uiCreator = inject(UICreatorService);
  private docServerUrlPipe = new DocServerUrlPipe();

  data = input.required<CustomerProfileData>();

  readonly editButtonEmitter = output<MouseEvent>();

  isLoading = signal(true);
  imgError = signal(false);

  hasImg = computed(() => (this.data()?.profilePicture?.trim()?.length ?? 0) > 0);

  private infos = toObservable(this.data).pipe(
    startWith({
      id: -1,
      fullName: '',
      profilePicture: undefined,
      loaded: false,
    }),
    switchMap((data) =>
      iif(
        () => !!data.profilePicture,
        defer(() =>
          from(this.docServerUrlPipe.transform(this.data()?.profilePicture)).pipe(
            map((res) => ({ ...data, profilePicture: res }))
          )
        ),
        defer(() => of(data))
      )
    ),
    map((data) => ({
      id: data?.id,
      fullname: data.fullName,
      pictureURL: data.profilePicture,
      loaded: true,
    }))
  );

  avatarInfos = toSignal(this.infos);

  editBtnClick(event: MouseEvent): void {
    this.editButtonEmitter.emit(event);
  }

  changeLoadingState(state: boolean): void {
    this.isLoading.set(state);
  }
}
